<template v-cloak>
  <nav v-if="hasPagination" :aria-label="labels.ariaLabel" class="pagination">
    <ul class="pagination__list">
      <li>
        <button @click="setPrev"
                :disabled="currentPage === 1"
                class="prev pagination__item"
                :aria-disabled="currentPage === 1">
          <Icon icon="chevron-left" :aria-label="labels.prev"/>
        </button>
      </li>
      <li v-for="(link, index) in pagination.links"
          :key="index"
          :class="{ 'active': link.active, 'maybe-hide': maybeHide(index) }"
          :hidden="maybeHideItem && maybeHide(index) && !link.active"
      >
        <button
            v-if="!link.disabled || !!link.active"
            @click.prevent="setPage(link.page)"
            class="pagination__item"
            :aria-current="currentPage === link.page ? 'page' : false"
            :disabled="currentPage === link.page">
          <span class="sr-only" v-html="labels.page" /> {{ link.text }}
        </button>
        <span v-else v-html="link.text"/>
      </li>
      <li>
        <button
            @click="setNext"
            :disabled="currentPage === totalPages"
            :aria-disabled="currentPage === totalPages"
            class="next pagination__item">
          <Icon icon="chevron-right" :aria-label="labels.next"/>
        </button>
      </li>
    </ul>
  </nav>
</template>
<script>
import { defineComponent, computed, inject } from 'vue';
import Icon from "./Icon.vue";

export default defineComponent( {
  name: 'Pagination',
  components: {
    Icon
  },
  setup( props, { emit } ) {
    // Inject parent properties
    const totalPages = inject( 'totalPages' );
    const currentPage = inject( 'currentPage' );

    const labels = inject( 'paginationLabels', {
      ariaLabel: 'Pagination',
      page: 'Page',
      prev: 'Previous page',
      next: 'Next page',
    } );

    // Compute whether pagination is needed
    const hasPagination = computed( () => totalPages.value > 1 );

    /*
    // Compute pagination links
    const pagination = computed( () => {
      const current = currentPage.value;
      const total = totalPages.value;
      const midSize = 1;
      const links = [];

      // Add the first page link
      links.push( {
        text: '1',
        page: 1,
        active: ( current === 1 ),
        disabled: ( current === 1 )
      } );

      // Determine the range of pages to show
      let start = Math.max( 2, current - midSize );
      let end = Math.min( current + midSize, total - 1 );

      // Add the dots if necessary
      if ( start > 2 ) {
        links.push( {
          text: '...',
          page: false,
          active: false,
          disabled: true
        } );
      }

      if( total === current ) {
        links.push( {
          text: total - 2,
          page: total - 2,
          active: false,
          disabled: false
        })
      }

      // Add the middle page links
      for ( let i = start; i <= end; i++ ) {
        links.push( {
          text: i.toString(),
          page: i,
          active: ( i === current ),
          disabled: false
        } );
      }

      if( 1 === current ) {
        links.push( {
          text: '3',
          page: 3,
          active: false,
          disabled: false
        })
      }

      // Add the dots if necessary
      if ( end < total - 1 ) {
        links.push( {
          text: '...',
          page: false,
          active: false,
          disabled: true
        } );
      }

      if ( total > 1 ) {
        links.push( {
          text: total.toString(),
          page: total,
          active: ( current === total ),
          disabled: ( current === total )
        } );
      }

      // Determine the previous and next pages
      const prev = ( current > 1 ) ? current - 1 : false;
      const next = ( current < total ) ? current + 1 : false;

      return {
        current,
        prev,
        next,
        links
      };
    } );

     */

    // Compute pagination links
    const pagination = computed(() => {
      const current = currentPage.value;
      const total = totalPages.value;
      const midSize = 1; // Number of adjacent pages to show around the current page
      const links = [];

      // Add the first page link
      links.push({
        text: '1',
        page: 1,
        active: current === 1,
        disabled: current === 1,
      });

      // Determine the range of pages to show
      let start = Math.max(2, current - midSize);
      let end = Math.min(current + midSize, total - 1);

      // Add dots before start if needed
      if (start > 2) {
        links.push({
          text: '...',
          page: false,
          active: false,
          disabled: true,
        });
      }

      // Add the middle page links (between start and end)
      for (let i = start; i <= end; i++) {
        links.push({
          text: i.toString(),
          page: i,
          active: i === current,
          disabled: false,
        });
      }

      // Add dots after end if needed
      if (end < total - 1) {
        links.push({
          text: '...',
          page: false,
          active: false,
          disabled: true,
        });
      }

      // Add the last page link
      if (total > 1) {
        links.push({
          text: total.toString(),
          page: total,
          active: current === total,
          disabled: current === total,
        });
      }

      // Determine the previous and next pages
      const prev = current > 1 ? current - 1 : false;
      const next = current < total ? current + 1 : false;

      return {
        current,
        prev,
        next,
        links,
      };
    });

    const setPage = ( page ) => {
      emit( 'update:currentPage', parseInt( page ) );
    };

    const setPrev = () => {
      if ( pagination.value.prev ) {
        setPage( pagination.value.prev );
      }
    };

    const setNext = () => {
      if ( pagination.value.next ) {
        setPage( pagination.value.next );
      }
    };

    // Function to determine when to apply the 'maybe-hide' class
    const maybeHide = ( index ) => {
      const activeIndex = pagination.value.links.findIndex( link => link.active );
      return index === activeIndex - 1 || index === activeIndex + 1;
    };

    const maybeHideItem = computed( () => {
      // Hide pagination items on 400% zoom level.
      return window.matchMedia('(resolution: 4dppx)').matches && pagination.value.links.length > 5;
    } );

    return {
      labels,
      totalPages,
      currentPage,
      hasPagination,
      pagination,
      setPrev,
      setNext,
      setPage,
      maybeHide,
      maybeHideItem,
    };
  }
} );
</script>
